import { useMemo } from 'react';

import { YamlLineBase } from './YamlLineBase';
import { StyledScroller, SyntaxHighlight } from './YamlStyledComponents';
import { PreviewStateProvider } from '../../../_providers/PreviewStateProvider';
import { useCodeHighlighter } from '../../../hooks/useCodeHighlighter';
import { PreviewYamlProps } from '../../types';

const splitIndentation = (line: string) => {
  const firstNonWhitespaceIndex = line.search(/\S/);

  if (firstNonWhitespaceIndex === -1) {
    return { indentation: line, content: '' };
  }

  return {
    indentation: line.slice(0, firstNonWhitespaceIndex),
    content: line.slice(firstNonWhitespaceIndex),
  };
};

export const YamlRawContainer = (props: PreviewYamlProps) => {
  const { children, prefix, containerSx, data } = props;
  const rawText = data as string;

  const highlighter = useCodeHighlighter({
    language: 'yaml',
    enabled: true,
  });

  const rawLines = useMemo(() => rawText.split('\n'), [rawText]);

  const rawLineParts = useMemo(
    () => rawLines.map(splitIndentation),
    [rawLines]
  );

  const highlightedContent = useMemo(() => {
    if (!highlighter) return null;
    return rawLineParts.map(({ indentation, content }) => ({
      indentation,
      html: content ? highlighter.highlight(content) : '',
    }));
  }, [highlighter, rawLineParts]);

  const linesCount = rawLines.length;

  return (
    <PreviewStateProvider formattedCode={rawText}>
      {children ? (
        typeof children === 'function' ? (
          <>{children({ formattedCode: rawText })}</>
        ) : (
          <>{children}</>
        )
      ) : null}

      <StyledScroller ownerState={props}>
        <SyntaxHighlight ownerState={props} sx={containerSx}>
          {prefix}
          {rawLines.map((_, index) => {
            const { indentation, content } = rawLineParts[index];
            const highlighted = highlightedContent?.[index];

            const indentationEl = (
              <span className="DsPreviewYaml-Indentation">
                {highlighted?.indentation ?? indentation}
              </span>
            );

            const contentEl = highlighted ? (
              <span dangerouslySetInnerHTML={{ __html: highlighted.html }} />
            ) : (
              <span>{content}</span>
            );

            return (
              <YamlLineBase
                key={index}
                lineNumber={index + 1}
                linesCount={linesCount}
                showLineNumbers={props.showLineNumbers}
                indentation={indentationEl}
                content={contentEl}
              />
            );
          })}
        </SyntaxHighlight>
      </StyledScroller>
    </PreviewStateProvider>
  );
};
